import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Form, Input, Select, Tooltip, notification, DatePicker } from "antd";
import { LocalStorageService } from "../../../../../base/services/local-storage.service";
import { httpService } from "../../../../../base/services/httpService.service";
import BasicModal from "../../../../../ui/BasicModal/BasicModal";
import { withErrorBoundary } from "../../../../../components/ErrorBoundary/withErrorBoundary";

const { TextArea } = Input;
const { RangePicker } = DatePicker;

export default function ProjectTypes() {
   const workspcId = LocalStorageService?.getItem("currentWorkspaceId") || "";

   const { id } = useParams();
   const navigate = useNavigate();

   const [isModalOpen, setIsModalOpen] = useState(false);
   const [load, setLoad] = useState(false);
   const [dataList, setDataList] = useState([]);
   const [countries, setCountries] = useState([]);

   useEffect(() => {
      httpService.get("/Members/getAllOrgMembersInWorkspace/?WorkSpaceId=" + workspcId, (res) => {
         setDataList(
            res.data.map((member) => ({
               id: member.id,
               fullName: member.fullName,
               role: member.roles.map((role) => role.name)
            }))
         );
      });
      httpService.get("/Languages/getAllCountries", (res) => {
         setCountries(
            res.data
               .map((country) => ({
                  id: country.id,
                  name: country.name,
                  role: country.code
               }))
               .sort((a, b) => a.name.localeCompare(b.name))
         );
      });
   }, [workspcId]);

   const onFinish = (values) => {
      setLoad(true);
      let formattedStartDate;
      let formattedEndDate;
      if (values.timeLine) {
         const [startDate, endDate] = values.timeLine;
         formattedStartDate = startDate.format();
         formattedEndDate = endDate.format();
      }
      const payload = {
         name: values.projectTitle,
         description: values.projectDescription,
         managerId: values.projectManager,
         members: values.dataCollectors,
         workspaceId: LocalStorageService?.getItem("currentWorkspaceId") || "",
         typeId: id,
         countryIds: values.country,
         startDate: formattedStartDate,
         endDate: formattedEndDate
      };
      httpService
         .post(
            "/Projects/add",
            payload,
            (res) => {
               const createdProjectId = res.data;

               LocalStorageService.setItem("currentProjectID", createdProjectId);
               navigate("/dashboard");
            },
            (error) => {
               if (
                  error.response.status === 400 &&
                  Array.isArray(error.response.data.errors) &&
                  error.response.data.errors.includes &&
                  error.response.data.errors.includes(
                     "You reached your packet limit for creating projects in workspace!!"
                  )
               ) {
                  setIsModalOpen(true);
               } else {
                  notification.error({
                     message: "Error",
                     description: "Please try again"
                  });
               }
               setLoad(false);
            }
         )
         .finally(() => {
            setLoad(false);
         });
   };

   const onFinishFailed = (errorInfo) => {
      setLoad(false);
   };

   const textAreaStyle = { resize: "none" };

   return (
      <div className="create-new-project-page flex-column align-items-center ">
         <h2
            className="text-center d-flex align-items-center justify-content-center mt-3"
            style={{ padding: "50px" }}
         >
            New project
         </h2>

         <Form name="basic" onFinish={onFinish} onFinishFailed={onFinishFailed} autoComplete="off">
            <Form.Item
               label="Project Title*"
               name="projectTitle"
               rules={[
                  {
                     required: true,
                     message: "Please specify the official title of the project."
                  }
               ]}
            >
               <Input placeholder="Enter a title" />
            </Form.Item>

            <Form.Item
               label="Project Description"
               name="projectDescription"
               rules={[
                  {
                     required: false,
                     message: "Please furnish a detailed description of the project for clarity."
                  }
               ]}
            >
               <TextArea
                  rows="6"
                  style={textAreaStyle}
                  placeholder="Enter a description"
               ></TextArea>
            </Form.Item>

            <Form.Item
               label="Project Manager*"
               name="projectManager"
               rules={[
                  {
                     required: true,
                     message: "Specify the name and designation of the assigned project manager."
                  }
               ]}
            >
               <Select
                  placeholder="--Select a manager--"
                  allowClear
                  showSearch
                  optionFilterProp="children"
               >
                  {Array.isArray(dataList) &&
                     dataList
                        .filter(
                           (member) =>
                              member.role[0] !== "DataCollector" && member.role[0] !== "Guest"
                        )
                        .map((data, i) => {
                           return (
                              <Select.Option value={data.id} key={i}>
                                 {data.fullName}
                              </Select.Option>
                           );
                        })}
               </Select>
            </Form.Item>
            <Form.Item
               label="Team Members"
               name="dataCollectors"
               rules={[
                  {
                     required: false,
                     message:
                        "For documentation purposes, provide a roster of project team members."
                  }
               ]}
            >
               <Select
                  placeholder="--Select members-- "
                  mode="multiple"
                  showSearch
                  optionFilterProp="children"
               >
                  {dataList.map((data, i) => (
                     <Select.Option value={data.id} key={i}>
                        {data.fullName}
                     </Select.Option>
                  ))}
               </Select>
            </Form.Item>
            <Form.Item
               label="Country*"
               name="country"
               rules={[
                  {
                     required: true,
                     message: "Specify the country of origin or primary activity for the project."
                  }
               ]}
            >
               <Select
                  placeholder="--Select countries-- "
                  mode="multiple"
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                     option.children.toLowerCase().startsWith(input.toLowerCase())
                  }
               >
                  {countries.map((data, i) => {
                     return (
                        <Select.Option value={data.id} key={i}>
                           {data.name}
                        </Select.Option>
                     );
                  })}
               </Select>
            </Form.Item>
            <Form.Item
               label="Timeline*"
               name="timeLine"
               rules={[
                  {
                     required: true,
                     message: "Please select a timeline for the project."
                  }
               ]}
            >
               <RangePicker />
            </Form.Item>

            <div className="create-survey-button-group" style={{ marginTop: "30px" }}>
               <Form.Item
                  wrapperCol={{
                     offset: 8,
                     span: 16
                  }}
               >
                  <Button type="primary" onClick={() => navigate(-1)} className="defaultButton">
                     Cancel
                  </Button>
               </Form.Item>

               <Form.Item
                  wrapperCol={{
                     offset: 8,
                     span: 16
                  }}
               >
                  <Button
                     disabled={load}
                     loading={load}
                     htmlType="submit"
                     className="primaryButton"
                  >
                     Create project
                  </Button>
               </Form.Item>
            </div>
         </Form>
         <BasicModal isModalOpen={isModalOpen} handleCancel={() => setIsModalOpen(false)}>
            <h3
               style={{
                  textAlign: "center"
               }}
            >
               You reached your packet limit for creating projects in workspace!
            </h3>
         </BasicModal>
      </div>
   );
}

const ProjectTypesWithErrorBoundary = withErrorBoundary(ProjectTypes);
export { ProjectTypesWithErrorBoundary as ProjectTypes };
